import React, { useEffect, useState } from 'react';
import {
	CustomDocumentsResourceService,
	CustomPaymentResourceService,
	CustomRewardResourceService,
	DocumentsDTO,
	PaymentDTO,
	RewardDTO,
	SignallerDTO
} from '@/openapi';
import Button from '@/components/UI/Button';
import SuccessToast from '@/components/UI/SuccessToast';
import ConfirmationDialog from '@/components/UI/ConfirmationDialog';
import styles from './SignallerCard.module.css';
import { useTranslation } from 'react-i18next';
import { ReactComponent as File } from '@Assets/svg/file.svg';
import DocumentViewer from '../DocumentViewer';
import Modal from '../UI/Modal';
import { useRemoteGet } from '@/hooks/useRemoteGet';
import Loader from '../UI/Loader';
import { createPortal } from 'react-dom';
import { useRemotePost } from '@/hooks/useRemotePost';

interface SignallerCardProps {
	signaller: SignallerDTO;
	payFunction: 'pay' | 'reward';
	onPaymentSuccess?: () => void; // Callback per aggiornare la lista post pagamento
}

const SignallerCard: React.FC<SignallerCardProps> = ({ signaller, payFunction, onPaymentSuccess }) => {
	const { t } = useTranslation();

	const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
	const [showSuccessToast, setShowSuccessToast] = useState<boolean>(false);

	const [showDocumentModal, setShowDocumentModal] = useState<boolean>(false);
	const [selectedDocument, setSelectedDocument] = useState<{
		type: 'front' | 'back';
		data: string;
		contentType: string;
	} | null>(null);

	const findDocument = useRemoteGet<DocumentsDTO>({
		remoteMethod: async () => {
			if (!signaller.id) {
				throw new Error('User ID not available');
			}
			return CustomDocumentsResourceService.findDocumentsBySignaller({
				signallerId: signaller.id
			});
		},
		lazy: true
	});

	const createPayment = useRemotePost<PaymentDTO, { signallerId: number }>({
		remoteMethod: CustomPaymentResourceService.createPayment1,
		lazy: true
	});

	const createReward = useRemotePost<RewardDTO, { signallerId: number }>({
		remoteMethod: CustomRewardResourceService.payReward,
		lazy: true
	});

	const handleMarkAsPaid = () => {
		setShowConfirmDialog(true);
	};

	const handleConfirmPayment = async () => {
		if (signaller.id) {
			try {
				if (payFunction === 'pay') {
					await createPayment.fetch({ signallerId: signaller.id });
				} else {
					await createReward.fetch({ signallerId: signaller.id });
				}
				setShowConfirmDialog(false);
				setShowSuccessToast(true);
				// Chiama la callback dopo il successo dell'operazione
				onPaymentSuccess?.();
			} catch (error) {
				console.error('Errore durante il pagamento:', error);
				setShowConfirmDialog(false);
			}
		}
	};

	const handleCancelPayment = () => {
		setShowConfirmDialog(false);
	};

	const handleCloseSuccessToast = () => {
		setShowSuccessToast(false);
	};

	const handleViewDocument = (type: 'front' | 'back') => {
		if (type === 'front') {
			const data = findDocument.data?.frontDoc;
			const contentType = findDocument.data?.frontDocContentType;
			if (data && contentType) {
				setSelectedDocument({ type, data, contentType });
				setShowDocumentModal(true);
			}
		} else {
			const data = findDocument.data?.backDoc;
			const contentType = findDocument.data?.backDocContentType;
			if (data && contentType) {
				setSelectedDocument({ type, data, contentType });
				setShowDocumentModal(true);
			}
		}
	};

	const handleCloseDocumentModal = () => {
		setShowDocumentModal(false);
		setSelectedDocument(null);
	};

	useEffect(() => {
		if (signaller.id) {
			findDocument.fetch();
		}
	}, [signaller.id]);

	return (
		<div className={styles.signallerCard}>
			<Loader visible={findDocument.loading || createPayment.loading || createReward.loading} />
			<div className={styles.cardHeader}>
				<div className={styles.userInfo}>
					<span className={styles.username}>{signaller.userDTO?.login || 'N/A'}</span>
				</div>
				<div className={styles.contractsInfo}>
					<span className={styles.contractsBadge}>
						{t('adminPage.signaller.contracts')}: {signaller.closedContracts || 0}
					</span>
					{signaller.contractsCurrentCycle !== undefined && (
						<span className={styles.cycleBadge}>
							{t('adminPage.signaller.cycle')}: {signaller.contractsCurrentCycle}
						</span>
					)}
				</div>
			</div>
			<div className={styles.cardBody}>
				<div className={styles.infoGrid}>
					<div className={styles.infoItem}>
						<span className={styles.label}>{t('profile.mail')}:</span>
						<span className={styles.value}>{signaller.userDTO?.email || 'N/A'}</span>
					</div>

					<div className={styles.infoItem}>
						<span className={styles.label}>{t('profile.phone')}:</span>
						<span className={styles.value}>{signaller?.phone || 'N/A'}</span>
					</div>

					<div className={styles.infoItem}>
						<span className={styles.label}>{t('profile.iban')}:</span>
						<span className={styles.value}>{signaller?.iban || 'N/A'}</span>
					</div>

					<div className={styles.infoItem}>
						<span className={styles.label}>{t('profile.taxCode')}:</span>
						<span className={styles.value}>{signaller?.taxCode || 'N/A'}</span>
					</div>
				</div>
			</div>
			<div className={styles.cardFooter}>
				<div className={styles.documentsSection}>
					<div className={styles.docInfo}>
						<span className={styles.docLabel}>{t('adminPage.signaller.frontDoc')}</span>
						<button className={styles.viewIcon} onClick={() => handleViewDocument('front')} type="button">
							<File className={styles.eyeIcon} />
						</button>
					</div>
					<div className={styles.docInfo}>
						<span className={styles.docLabel}>{t('adminPage.signaller.backDoc')}</span>
						<button className={styles.viewIcon} onClick={() => handleViewDocument('back')} type="button">
							<File className={styles.eyeIcon} />
						</button>
					</div>{' '}
				</div>

				<Button
					className={styles.paidButton}
					name={payFunction === 'pay' ? t('adminPage.signaller.payBtn') : t('adminPage.signaller.rewardBtn')}
					onClick={handleMarkAsPaid}
					typeSubmit="button"
					background="black"
					color="white"
				/>
			</div>
			{/* Modale per visualizzare i documenti*/}
			{showDocumentModal &&
				createPortal(
					<Modal isOpen={showDocumentModal} onClose={handleCloseDocumentModal}>
						{selectedDocument && (
							<DocumentViewer
								type={selectedDocument.type}
								data={selectedDocument.data}
								contentType={selectedDocument.contentType}
							/>
						)}
					</Modal>,
					document.body // Renderizza il modale direttamente nel body per occupare la pagina intera
				)}
			{/* Dialog di conferma pagamento */}
			<ConfirmationDialog
				isOpen={showConfirmDialog}
				title={payFunction === 'pay' ? t('signallerCard.confirmPay') : t('signallerCard.confirmReward')}
				message={payFunction === 'pay' ? t('signallerCard.confirmPayMessage') : t('signallerCard.confirmRewardMessage')}
				confirmText={payFunction === 'pay' ? t('adminPage.signaller.payBtn') : t('adminPage.signaller.rewardBtn')}
				cancelText={t('common.cancel')}
				onConfirm={handleConfirmPayment}
				onCancel={handleCancelPayment}
				isLoading={createPayment.loading || createReward.loading}
			/>

			<SuccessToast
				isVisible={showSuccessToast}
				message={payFunction === 'pay' ? t('signallerCard.paymentSuccess') : t('signallerCard.rewardSuccess')}
				onClose={handleCloseSuccessToast}
				type={'success'}
			/>
		</div>
	);
};

export default SignallerCard;
